import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { load as loadCashfree } from "@cashfreepayments/cashfree-js";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill } from "../../components/common/Primitives";
import Price from "../../components/common/Price";
import { Button } from "../../components/ui/button";
import { CheckCircle2, Package, ShoppingCart, Loader2, Sparkles, Eye, Download, X } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const inr = (v) => "₹" + Number(v).toLocaleString("en-IN");

const PreviewModal = ({ orderId, doc, onClose }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const isPdf = (doc?.content_type || "").includes("pdf");

  useEffect(() => {
    let objectUrl = null;
    api
      .get(`/daas/orders/${orderId}/deliverables/${doc.doc_id}/download?inline=true`, { responseType: "blob" })
      .then((resp) => {
        const blob = new Blob([resp.data], { type: doc.content_type || "application/octet-stream" });
        objectUrl = window.URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
    return () => {
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, doc.doc_id]);

  const download = () => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = doc.filename;
    a.click();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy/70 backdrop-blur-sm p-4"
      data-testid="deliverable-preview-modal"
    >
      <div className="bg-white rounded-md shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden">
        <div className="border-b border-navy/[0.08] px-5 py-3 flex items-center justify-between flex-shrink-0">
          <div>
            <div className="overline text-ink-muted">Preview</div>
            <div className="font-heading text-lg text-ink">{doc.filename}</div>
            <div className="text-[10px] font-mono text-ink-muted mt-0.5">
              {doc.content_type} · {Math.round((doc.size || 0) / 1024)} KB
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={download}
              disabled={!url}
              className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded bg-navy text-white hover:bg-navy/90 disabled:opacity-40"
              data-testid="preview-download-btn"
            >
              <Download size={12} className="inline mr-1" />
              Download
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded hover:bg-navy/[0.05]"
              data-testid="preview-close-btn"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden bg-navy/[0.02]">
          {loading && (
            <div className="flex items-center justify-center h-full text-ink-muted">
              <Loader2 className="animate-spin mr-2" size={16} /> Preparing preview…
            </div>
          )}
          {!loading && url && isPdf && (
            <iframe
              src={url}
              title={doc.filename}
              className="w-full h-full border-0"
              data-testid="preview-iframe"
            />
          )}
          {!loading && url && !isPdf && (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center">
              <div className="text-ink-muted text-sm mb-4">
                Inline preview is not supported for this file type.
              </div>
              <button
                onClick={download}
                className="px-4 py-2 rounded bg-navy text-white text-sm"
              >
                <Download size={14} className="inline mr-2" />
                Download to view
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function StartupDiligenceServices() {
  const [catalog, setCatalog] = useState({ modules: [], packages: [] });
  const [orders, setOrders] = useState([]);
  const [selectedModules, setSelectedModules] = useState(new Set());
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [quote, setQuote] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [preview, setPreview] = useState(null);

  const loadOrders = () =>
    api.get("/startup/daas/orders").then((r) => setOrders(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/daas/catalog").then((r) => setCatalog(r.data)).catch(() => {});
    loadOrders();
  }, []);

  useEffect(() => {
    if (!selectedPackage && selectedModules.size === 0) {
      setQuote(null);
      return;
    }
    const payload = selectedPackage
      ? { package_id: selectedPackage, module_ids: [] }
      : { module_ids: Array.from(selectedModules) };
    api.post("/daas/quote", payload).then((r) => setQuote(r.data)).catch(() => setQuote(null));
  }, [selectedModules, selectedPackage]);

  const toggleModule = (id) => {
    setSelectedPackage(null);
    setSelectedModules((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const pickPackage = (id) => {
    setSelectedModules(new Set());
    setSelectedPackage((p) => (p === id ? null : id));
  };

  const placeOrder = async () => {
    if (!quote) return;
    setPlacing(true);
    try {
      const payload = selectedPackage
        ? { package_id: selectedPackage }
        : { module_ids: Array.from(selectedModules) };
      const { data } = await api.post("/startup/daas/orders", payload);
      toast.success(`Order ${data.order_id} created`);
      const pay = await api.post(`/startup/daas/orders/${data.order_id}/pay`);
      if (pay.data?.payment_session_id) {
        const cashfree = await loadCashfree({ mode: pay.data.cashfree_mode || "sandbox" });
        cashfree.checkout({
          paymentSessionId: pay.data.payment_session_id,
          redirectTarget: "_self",
        });
        return;
      }
      // Fallback (Cashfree unavailable — order auto-marked paid on backend).
      toast.info(pay.data?.reason
        ? `Payment marked paid (Cashfree offline: ${pay.data.reason.slice(0, 80)}). VenturaZ team will begin work.`
        : "Payment recorded. VenturaZ team will begin work.");
      setSelectedModules(new Set());
      setSelectedPackage(null);
      loadOrders();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPlacing(false);
    }
  };

  return (
    <DashboardShell
      title="Diligence Services"
      subtitle="STARTUP · MONETISABLE DILIGENCE MODULES"
    >
      <Toaster richColors position="top-right" />

      {/* Packages */}
      <div className="mb-6">
        <div className="overline mb-3">Value Packages</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {catalog.packages.map((p) => (
            <div
              key={p.package_id}
              onClick={() => pickPackage(p.package_id)}
              data-testid={`daas-package-${p.package_id}`}
              className={
                "border rounded-md p-5 cursor-pointer transition relative " +
                (selectedPackage === p.package_id
                  ? "border-gold bg-gold/[0.06] ring-2 ring-gold"
                  : "border-navy/[0.1] bg-white hover:border-navy/[0.25]")
              }
            >
              {p.badge && (
                <div className="absolute -top-2 right-4">
                  <Pill tone="gold">{p.badge}</Pill>
                </div>
              )}
              <div className="flex items-center gap-2 mb-1">
                <Package size={16} className="text-navy" />
                <div className="font-heading text-lg text-ink">{p.name}</div>
              </div>
              <p className="text-xs text-ink-muted mb-3">{p.description}</p>
              <div className="mb-3">
                {p.modules.map((mid) => {
                  const mod = catalog.modules.find((m) => m.module_id === mid);
                  return (
                    <div key={mid} className="text-[11px] text-ink flex items-center gap-1 mb-0.5">
                      <CheckCircle2 size={11} className="text-emerald-600" />
                      {mod?.name || mid}
                    </div>
                  );
                })}
              </div>
              <div className="mt-3">
                <Price
                  price={p.price_inr}
                  originalPrice={p.list_price_inr}
                  size="md"
                  testId={`daas-package-price-${p.package_id}`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modules */}
      <div className="mb-6">
        <div className="overline mb-3">À la carte modules</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {catalog.modules.map((m) => {
            const isSelected = selectedModules.has(m.module_id);
            const isInPackage =
              selectedPackage &&
              catalog.packages.find((p) => p.package_id === selectedPackage)?.modules?.includes(m.module_id);
            return (
              <div
                key={m.module_id}
                onClick={() => toggleModule(m.module_id)}
                data-testid={`daas-module-${m.module_id}`}
                className={
                  "border rounded-md p-4 cursor-pointer transition " +
                  (isSelected
                    ? "border-navy bg-navy/[0.04] ring-2 ring-navy/40"
                    : isInPackage
                    ? "border-emerald-500/50 bg-emerald-50 opacity-70"
                    : "border-navy/[0.1] bg-white hover:border-navy/[0.25]")
                }
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-heading text-ink">{m.name}</div>
                    <div className="text-[10px] font-mono uppercase text-ink-muted tracking-wider">
                      {m.category} · {m.duration_days}d turnaround
                    </div>
                  </div>
                  <Price
                    price={m.price_inr}
                    originalPrice={m.list_price_inr}
                    size="sm"
                    align="right"
                    testId={`daas-module-price-${m.module_id}`}
                  />
                </div>
                <p className="text-xs text-ink-muted mt-2">{m.description}</p>
                {isInPackage && (
                  <div className="mt-2 text-[10px] text-emerald-700 font-mono uppercase tracking-wider">
                    Included in selected package
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Quote / place */}
      {quote && (
        <Card testId="daas-quote-card" className="mb-6 border-gold bg-gold/[0.04]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="overline">Your Selection</div>
              <div className="text-sm text-ink mt-1">
                {selectedPackage ? "Package" : `${selectedModules.size} module(s)`}
              </div>
            </div>
            <div className="text-right">
              <div className="font-heading text-3xl text-ink">{inr(quote.total_inr)}</div>
              {quote.savings_inr > 0 && (
                <div className="text-xs text-emerald-700 font-mono">
                  You save {inr(quote.savings_inr)}
                </div>
              )}
            </div>
            <Button
              onClick={placeOrder}
              disabled={placing}
              data-testid="daas-place-order-btn"
              className="min-w-[200px]"
            >
              {placing ? (
                <Loader2 className="animate-spin" size={14} />
              ) : (
                <>
                  <ShoppingCart size={14} className="mr-2" />
                  Confirm & Pay
                </>
              )}
            </Button>
          </div>
        </Card>
      )}

      {/* My Orders */}
      <Card testId="daas-orders-card">
        <div className="overline mb-3">Your Orders</div>
        {orders.length === 0 ? (
          <div className="text-ink-muted text-sm italic">No orders yet. Pick a module or package above.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] font-mono uppercase text-ink-muted tracking-wider">
                <th className="text-left py-2">Order</th>
                <th className="text-left py-2">Modules</th>
                <th className="text-right py-2">Amount</th>
                <th className="text-right py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr
                  key={o.order_id}
                  className="border-t border-navy/[0.06]"
                  data-testid={`daas-order-row-${o.order_id}`}
                >
                  <td className="py-3 font-mono text-xs">{o.order_id}</td>
                  <td className="py-3 text-xs text-ink-muted">
                    {(o.module_ids || []).map((m) => m.replace(/_/g, " ")).join(", ")}
                  </td>
                  <td className="py-3 text-right font-mono">{inr(o.total_inr)}</td>
                  <td className="py-3 text-right">
                    <Pill
                      tone={
                        o.status === "delivered"
                          ? "success"
                          : o.status === "cancelled"
                          ? "danger"
                          : o.status === "paid" || o.status === "in_progress"
                          ? "gold"
                          : "muted"
                      }
                    >
                      {o.status.replace(/_/g, " ")}
                    </Pill>
                    {(o.deliverables || []).length > 0 && (
                      <div
                        className="mt-2 flex flex-wrap gap-1 justify-end"
                        data-testid={`daas-deliverables-${o.order_id}`}
                      >
                        {o.deliverables.map((d) => (
                          <div key={d.doc_id} className="inline-flex items-center gap-1">
                            <button
                              onClick={() => setPreview({ orderId: o.order_id, doc: d })}
                              data-testid={`daas-preview-${d.doc_id}`}
                              className="text-[10px] font-mono uppercase tracking-wider bg-gold/20 hover:bg-gold/40 text-navy px-2 py-1 rounded inline-flex items-center gap-1"
                              title={`Preview ${d.filename}`}
                            >
                              <Eye size={10} /> Preview
                            </button>
                            <button
                              onClick={async () => {
                                try {
                                  const resp = await api.get(
                                    `/daas/orders/${o.order_id}/deliverables/${d.doc_id}/download`,
                                    { responseType: "blob" }
                                  );
                                  const url = window.URL.createObjectURL(new Blob([resp.data]));
                                  const a = document.createElement("a");
                                  a.href = url;
                                  a.download = d.filename;
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                } catch (e) {
                                  toast.error(formatApiError(e));
                                }
                              }}
                              data-testid={`daas-download-${d.doc_id}`}
                              className="text-[10px] font-mono uppercase tracking-wider bg-navy/[0.05] hover:bg-navy/[0.12] text-ink px-2 py-1 rounded"
                              title={`Download ${d.filename}`}
                            >
                              ↓ {d.filename.length > 22 ? d.filename.slice(0, 20) + "…" : d.filename}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {preview && (
        <PreviewModal
          orderId={preview.orderId}
          doc={preview.doc}
          onClose={() => setPreview(null)}
        />
      )}
    </DashboardShell>
  );
}
